

import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import CompanyLogo from './CompanyLogo';
import data from '../data.json';

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesMenuRef = useRef(null);
  const { menuItems, servicesList } = data;

  // Touch devices have no hover, so close the dropdown on a tap outside it
  useEffect(() => {
    if (!isServicesOpen) return;
    const handlePointerDown = (e) => {
      if (servicesMenuRef.current && !servicesMenuRef.current.contains(e.target)) {
        setIsServicesOpen(false);
        setHoveredItem(null);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isServicesOpen]);

  const menuVariants = {
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        when: 'afterChildren',
      },
    },
    open: {
      height: 'auto',
      opacity: 1,
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, y: -10 },
    open: { opacity: 1, y: 0 },
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    window.dispatchEvent(
      new CustomEvent('mobileMenuChange', { detail: !isOpen })
    );
  };

  return (
    <nav className="left-0 right-0 top-0 z-50 bg-white shadow-lg">
      <div className="max-w-7xl mx-auto p-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          <div className="flex items-center">
            <RouterLink to="/" className="text-white font-bold text-xl cursor-pointer">
              <CompanyLogo />
            </RouterLink>
          </div>
          {/* Navbar Menu */}
          <div className="hidden lg:flex items-center space-x-4">
            {menuItems.map((item) => {
              const linkClassName = `text-grey-900 hover:text-[#780606] px-3 py-2 transition-all duration-300 cursor-pointer ${
                hoveredItem && hoveredItem !== item.id ? 'opacity-50' : ''
              } ${
                hoveredItem === item.id
                  ? 'font-bold border-b-2 border-[#780606]'
                  : ''
              }`;

              if (item.to !== 'services') {
                return (
                  <RouterLink
                    key={item.id}
                    to={item.to === "/" ? item.to : `/${item.to}`}
                    className={linkClassName}
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {item.name}
                  </RouterLink>
                );
              }

              // Services opens a dropdown listing every service page
              return (
                <div
                  key={item.id}
                  ref={servicesMenuRef}
                  className="relative"
                  onMouseEnter={() => {
                    setHoveredItem(item.id);
                    setIsServicesOpen(true);
                  }}
                  onMouseLeave={() => {
                    setHoveredItem(null);
                    setIsServicesOpen(false);
                  }}
                  onKeyDown={(e) => e.key === 'Escape' && setIsServicesOpen(false)}
                >
                  <div className="flex items-center">
                    <RouterLink to={`/${item.to}`} className={linkClassName}>
                      {item.name}
                    </RouterLink>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      aria-label="Show services"
                      aria-haspopup="true"
                      aria-expanded={isServicesOpen}
                      className="-ml-2 p-2 text-gray-600 hover:text-[#780606] transition-colors duration-300"
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${
                          isServicesOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </div>
                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-full pt-3 w-80 z-50"
                      >
                        <div className="bg-white rounded-lg shadow-xl border-t-2 border-[#780606] py-2">
                          {servicesList.map((service) => (
                            <RouterLink
                              key={service.id}
                              to={`/services/${service.slug}`}
                              className="block border-l-4 border-transparent px-4 py-2 text-sm text-gray-700 hover:border-[#780606] hover:bg-[#780606]/10 hover:text-[#780606] hover:pl-5 focus:outline-none focus-visible:border-[#780606] focus-visible:bg-[#780606]/10 focus-visible:text-[#780606] transition-all duration-200"
                            >
                              {service.heading}
                            </RouterLink>
                          ))}
                          <div className="border-t border-gray-100 mt-2 pt-2">
                            <RouterLink
                              to="/services"
                              className="block px-4 py-2 text-sm font-semibold text-custom-blue hover:text-[#780606] transition-colors duration-300"
                            >
                              View All Services →
                            </RouterLink>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            {/* Contact Us Button */}
            <RouterLink
              to="/contact"
              className="bg-custom-blue hover:bg-[#780606] text-white py-2 px-4 rounded transition duration-300 cursor-pointer"
            >
              Contact Us
            </RouterLink>
          </div>
          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-[#0A2A71] focus:outline-none"
            >
              <svg
                className="h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                {isOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="lg:hidden bg-[#3d3c3b] p-4 overflow-hidden"
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <motion.div className="px-2 pt-2 pb-3 space-y-1">
              {menuItems.map((item) => (
                <motion.div key={item.id} variants={itemVariants}>
                  <div className="flex items-center">
                    <RouterLink
                      to={item.to === "/" ? item.to : `/${item.to}`}
                      className="flex-grow text-white hover:bg-white hover:text-black block px-3 py-2 text-sm font-medium cursor-pointer uppercase"
                      onClick={toggleMenu}
                    >
                      {item.name}
                    </RouterLink>
                    {item.to === 'services' && (
                      <button
                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                        aria-label="Show services"
                        aria-expanded={isMobileServicesOpen}
                        className="px-3 py-2 text-white hover:bg-white hover:text-black"
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-300 ${
                            isMobileServicesOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  {item.to === 'services' && isMobileServicesOpen && (
                    <div className="border-l-2 border-[#780606] ml-3 mt-1 mb-2">
                      {servicesList.map((service) => (
                        <RouterLink
                          key={service.id}
                          to={`/services/${service.slug}`}
                          className="text-gray-200 hover:bg-white hover:text-black block px-4 py-2 text-sm cursor-pointer"
                          onClick={toggleMenu}
                        >
                          {service.heading}
                        </RouterLink>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              <motion.div variants={itemVariants}>
                <RouterLink
                  to="/contact"
                  className="text-white hover:bg-[#780606] block px-3 py-2 text-sm font-medium cursor-pointer uppercase"
                  onClick={() => setIsOpen(false)}
                >
                  Contact Us
                </RouterLink>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default NavBar;